/**
 * Centralized logging utility for TakeFav
 * Provides consistent logging patterns with configurable levels
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

interface LogConfig {
  level: LogLevel
  enableConsole: boolean
  enableTimestamp: boolean
  enableContext: boolean
  maxContextLength: number
}

class Logger {
  private config: LogConfig = {
    level: LogLevel.INFO,
    enableConsole: true,
    enableTimestamp: true,
    enableContext: true,
    maxContextLength: 50,
  }

  private contextStack: string[] = []

  /**
   * Configure logger settings
   */
  configure(config: Partial<LogConfig>): void {
    this.config = { ...this.config, ...config }
  }

  /**
   * Set log level
   */
  setLevel(level: LogLevel): void {
    this.config.level = level
  }

  /**
   * Push context to stack
   */
  pushContext(context: string): void {
    this.contextStack.push(context)
  }

  /**
   * Pop context from stack
   */
  popContext(): string | undefined {
    return this.contextStack.pop()
  }

  /**
   * Get current context string
   */
  private getContextString(): string {
    if (!this.config.enableContext || this.contextStack.length === 0) {
      return ''
    }

    const context = this.contextStack.join(' > ')
    return context.length > this.config.maxContextLength
      ? `[${context.substring(0, this.config.maxContextLength)}...]`
      : `[${context}]`
  }

  /**
   * Format log message
   */
  private formatMessage(level: string, message: string, data?: any): string {
    const parts: string[] = []

    if (this.config.enableTimestamp) {
      parts.push(new Date().toISOString())
    }

    parts.push(`[${level.toUpperCase()}]`)

    const context = this.getContextString()
    if (context) {
      parts.push(context)
    }

    parts.push(message)

    return parts.join(' ')
  }

  /**
   * Log message if level is enabled
   */
  private log(level: LogLevel, levelName: string, message: string, data?: any): void {
    if (level < this.config.level) {
      return
    }

    const formattedMessage = this.formatMessage(levelName, message)

    if (this.config.enableConsole) {
      switch (level) {
        case LogLevel.DEBUG:
          console.debug(formattedMessage, data)
          break
        case LogLevel.INFO:
          console.info(formattedMessage, data)
          break
        case LogLevel.WARN:
          console.warn(formattedMessage, data)
          break
        case LogLevel.ERROR:
          console.error(formattedMessage, data)
          break
      }
    }
  }

  /**
   * Debug level logging
   */
  debug(message: string, data?: any): void {
    this.log(LogLevel.DEBUG, 'DEBUG', message, data)
  }

  /**
   * Info level logging
   */
  info(message: string, data?: any): void {
    this.log(LogLevel.INFO, 'INFO', message, data)
  }

  /**
   * Warning level logging
   */
  warn(message: string, data?: any): void {
    this.log(LogLevel.WARN, 'WARN', message, data)
  }

  /**
   * Error level logging
   */
  error(message: string, data?: any): void {
    this.log(LogLevel.ERROR, 'ERROR', message, data)
  }

  /**
   * API level logging
   */
  api(method: string, url: string, data?: any): void {
    this.log(LogLevel.INFO, 'API', `🌐 ${method} ${url}`, data)
  }

  /**
   * Group related log messages
   */
  group(label: string, callback: () => void): void {
    if (this.config.enableConsole && this.config.level <= LogLevel.INFO) {
      console.group(label)
      this.pushContext(label)

      try {
        callback()
      } finally {
        this.popContext()
        console.groupEnd()
      }
    } else {
      // Execute callback without grouping if console is disabled
      this.pushContext(label)
      try {
        callback()
      } finally {
        this.popContext()
      }
    }
  }

  /**
   * Time operation execution
   */
  time(label: string, callback: () => void | Promise<void>): void {
    if (this.config.enableConsole && this.config.level <= LogLevel.INFO) {
      console.time(label)
      this.pushContext(label)

      try {
        const result = callback()
        if (result instanceof Promise) {
          result.finally(() => {
            console.timeEnd(label)
            this.popContext()
          })
        } else {
          console.timeEnd(label)
          this.popContext()
        }
      } catch (error) {
        console.timeEnd(label)
        this.popContext()
        throw error
      }
    } else {
      // Execute callback without timing if console is disabled
      this.pushContext(label)
      try {
        const result = callback()
        if (result instanceof Promise) {
          result.finally(() => this.popContext())
        } else {
          this.popContext()
        }
      } catch (error) {
        this.popContext()
        throw error
      }
    }
  }

  /**
   * Create a child logger with context
   */
  withContext(context: string): Logger {
    const childLogger = new Logger()
    childLogger.configure(this.config)
    childLogger.contextStack = [...this.contextStack, context]
    return childLogger
  }

  /**
   * Get current configuration
   */
  getConfig(): Readonly<LogConfig> {
    return { ...this.config }
  }

  /**
   * Reset to default configuration
   */
  reset(): void {
    this.config = {
      level: LogLevel.INFO,
      enableConsole: true,
      enableTimestamp: true,
      enableContext: true,
      maxContextLength: 50,
    }
    this.contextStack = []
  }
}

// Create and export singleton instance
export const log = new Logger()

// Development mode configuration
if (import.meta.env.DEV) {
  log.configure({
    level: LogLevel.DEBUG,
    enableConsole: true,
    enableTimestamp: true,
    enableContext: true,
  })
}

// Production mode configuration
if (import.meta.env.PROD) {
  log.configure({
    level: LogLevel.WARN,
    enableConsole: false,
    enableTimestamp: true,
    enableContext: true,
  })
}

// Export Logger class for custom instances
export { Logger }
